import React, { useContext, useEffect, useCallback, Suspense } from 'react';
import { PersistGate } from "redux-persist/integration/react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from 'axios';

import { persister } from "./Store/store";
import { CombinedContext } from 'Context/CombinedContext';
import DomainContext from "Context/DomainContext";
import RenderContext from 'Context/RenderContext';
import { TypeCheckProvider } from 'Context/TypeCheckAndDataContext';

import { getStoreData, initDB } from './db';
import { useTranslation } from "react-i18next";

import AppRoutes from "./AppRoutes";
import BackdropLoader from "Components/BackdropLoader"
import Header from "Components/Header";
import CircularProgress from '@mui/material/CircularProgress';

import "./App.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import VisibleWarp from 'Context/VisibleWrapper';
import { ACTION__SUCCESS_QUOTE, ACTION__MAKE_TO_QUOTE_ID, ACTION_CUSTOMER__QUOTE__ID } from 'Store/action';
import { getCartItems } from 'Utilities';
import LocalStorageWatcher from 'Components/LocalStorageWatcher';

const AppScripts = React.lazy(() => import('./appScripts'));
const Footer = React.lazy(() => import('./Components/Footer'));

const App = () => {
  const loader = useSelector((state) => state?.loader);
  const makeToQuoteId = useSelector((state) => state?.makeToQuoteId);
  const customerQuoteId = useSelector((state) => state?.customerQuoteId);
  const successQuote = useSelector((state) => state?.successQuote);
  const customerDetails = useSelector((state) => state?.customerDetails?.id);
  const token = useSelector((state) => state?.token);
  const { isBackdropLoading } = useContext(CombinedContext);
  const { baseURL, defaultURL, storeId } = useContext(DomainContext);
  const { loadIfUser, loadPreRender } = useContext(RenderContext);
  const { i18n } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();


  const LoaderProgress = () => {
    return (
      <div style={{ zIndex: 99, background: "rgba(0,0,0,0.08)" }} className="fixed top-0 left-0 w-1/1 h-1/1 flex center middle">
        <CircularProgress />
      </div>
    )
  };
  const handleInitDB = useCallback(async () => {
    await initDB();
  }, []);

  useEffect(() => {
    handleInitDB();
    getStoreData("recentUser");
  }, [handleInitDB]);

  const fetchDataEveryTwoMinutes = useCallback(async () => {
    if (!defaultURL) return;

    const localCache = localStorage.getItem("localCache");
    try {
      const response = await axios.get(`${defaultURL}/home/getCache`);
      const newCache = response?.data?.[0]?.cache;
      if (newCache !== localCache) {
        localStorage.setItem("localCache", newCache);
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        window.location.reload();
      }
    } catch (error) {
      console.error("Error fetching cache data:", error);
    }
  }, [defaultURL]);

  useEffect(() => {
    const intervalId = setInterval(fetchDataEveryTwoMinutes, 1 * 60 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [fetchDataEveryTwoMinutes]);


  useEffect(() => {
    const cleanHiddenSliders = () => {
      const hiddenContainers = document.querySelectorAll(
        '[aria-hidden="true"].keen-slider, [aria-hidden="true"].keenSliderOverflow, .slick-slide[aria-hidden="true"]'
      );

      hiddenContainers.forEach((el) => {
        el.removeAttribute('tabindex');
        el.removeAttribute('aria-hidden');

        const focusables = el.querySelectorAll(
          'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"]), div[tabindex]'
        );

        focusables.forEach((f) => {
          f.setAttribute('tabindex', '-1');
          if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(f.tagName)) {
            f.setAttribute('aria-hidden', 'true');
          }
        });
      });
    };

    cleanHiddenSliders();

    const observer = new MutationObserver(() => cleanHiddenSliders());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // On reload, reset to Dutch ("nl") and clear session storage
    i18n.changeLanguage("nl");
    sessionStorage.setItem("i18nextLng", "nl");
  }, []);

  useEffect(() => {
    document.querySelectorAll(".no-translate").forEach((el) => {
      el.setAttribute("translate", "no");
      el.setAttribute("lang", "en");
      el.classList.add("notranslate");
    });
  }, []);

  useEffect(() => {
    const triggerQuoteAPI = async () => {
      try {
        const payload = {
          quoteSubmittedId: customerQuoteId,
          currentquoteId: makeToQuoteId,
        };

        const response = await axios.post(`${defaultURL}/quote/checkoutToQuote`, payload);
        if (response?.data?.[0]?.success) {
          dispatch(ACTION_CUSTOMER__QUOTE__ID(response?.data?.[0]?.current_quote_id));
          dispatch(ACTION__MAKE_TO_QUOTE_ID(""));
          dispatch(ACTION__SUCCESS_QUOTE(""));
          getCartItems(dispatch, () => { }, customerQuoteId, customerDetails, () => { }, defaultURL, storeId, token);

        }
      } catch (err) {
        return
      }
    };

    if (defaultURL && makeToQuoteId && successQuote === "1" && !location.pathname.startsWith("/checkout")) {
      triggerQuoteAPI();
    }
  }, [location.pathname, defaultURL]);

  return (
    <div className="App">
      <PersistGate loading={null} persistor={persister}>
        {baseURL && (
          <TypeCheckProvider>
            <Header />
            <LocalStorageWatcher/>
            <AppRoutes />
            <VisibleWarp>
              <Footer />
            </VisibleWarp>
          </TypeCheckProvider>
        )}
      </PersistGate>
      {(loader || !baseURL) && <LoaderProgress />}

      {(!loader && baseURL && (loadIfUser || loadPreRender)) && (
        <Suspense fallback={null}>
          <AppScripts />
        </Suspense>
      )}

      {isBackdropLoading && <BackdropLoader />}
    </div>
  );
};

export default App;